import {
  forwardRef,
  PropsWithChildren,
  ReactNode,
  useState,
  useRef,
  useEffect,
} from 'react';

import { debounce } from '../01-core/Utilities';

export interface AnimatedDetailsProps extends PropsWithChildren {
  title?: string;
  /** Summary content */
  summary: string | ReactNode;
  /** Animation duration */
  duration?: number;
  /** Name of the details element */
  name?: string;
  /** HTML id, used as anchor target for hash links */
  id?: string;
  /** Whether the details should be shown */
  open?: boolean;
  /** Whether to calculate height of details element and store it as CSS variable */
  calculateHeight?: boolean;
  /** Additional class */
  className?: string;
  /** Additional summary class */
  summaryClassName?: string;
  /** Additional content class */
  contentClassName?: string;
  /** Reference to the content element */
  contentRef?: React.MutableRefObject<HTMLDivElement | null>;
  /** On toggle event handler */
  onToggle?: (state: boolean) => void;
  /** On key down event handler, attached to the details element */
  onKeyDown?: (e: React.KeyboardEvent<HTMLDetailsElement>) => void;
}

/** An animated details element */
export const AnimatedDetails = forwardRef<
  HTMLDetailsElement,
  AnimatedDetailsProps
>(
  (
    {
      summary,
      duration = 500,
      children,
      name,
      open,
      calculateHeight,
      className,
      summaryClassName,
      contentClassName,
      contentRef,
      onToggle,
      ...props
    }: AnimatedDetailsProps,
    ref,
  ) => {
    const _detailsRef = useRef<HTMLDetailsElement>(null);
    const _details =
      (ref as React.MutableRefObject<HTMLDetailsElement | null>) || _detailsRef;
    const _summary = useRef<HTMLElement>(null);
    const _contentRef = useRef<HTMLDivElement>(null);
    const _content = contentRef || _contentRef;
    const timer = useRef<number | null>(null);

    const [state, setState] = useState({
      isOpen: open,
      isClosing: false,
      isOpening: false,
    });

    const recalculateHeight = () => {
      if (!_details.current || !_summary.current || !_content.current) return;
      const summaryHeight = _summary.current.getBoundingClientRect().height;
      const isOpen = _details.current.open;

      _details.current.style.transition = 'none';
      _content.current.style.transition = 'none';

      if (!isOpen) {
        _details.current.style.overflow = 'hidden';
        _details.current.style.height = summaryHeight + 'px';
        _details.current.open = true;
        _details.current.classList.add('open');
      }

      _content.current.style.height = 'auto';

      const contentHeight = _content.current.getBoundingClientRect().height;

      if (!isOpen) {
        _details.current.open = false;
        _details.current.classList.remove('open');
      }

      _details.current.removeAttribute('style');
      _content.current.removeAttribute('style');

      _details.current.style.setProperty('--height', contentHeight + 'px');
    };

    const doClose = () => {
      if (timer.current != null) {
        window.clearTimeout(timer.current);
      }

      setState({
        isOpen: false,
        isClosing: true,
        isOpening: false,
      });

      if (onToggle) {
        onToggle(false);
      }
    };

    const doOpen = () => {
      if (timer.current != null) {
        window.clearTimeout(timer.current);
      }

      setState({
        isOpen: true,
        isOpening: true,
        isClosing: false,
      });

      if (onToggle) {
        onToggle(true);
      }
    };

    const onAnimationFinish = (open: boolean) => {
      if (!_details.current) {
        return;
      }

      setState({
        isOpen: open,
        isClosing: false,
        isOpening: false,
      });
    };

    const onClick: React.MouseEventHandler<HTMLElement> = (e) => {
      e.preventDefault();
      if (state.isClosing || !state.isOpen) {
        doOpen();
      } else if (state.isOpening || state.isOpen) {
        doClose();
      }
    };

    const handleResize = debounce(
      () => {
        recalculateHeight();
      },
      100,
      false,
    );

    useEffect(() => {
      if (calculateHeight) {
        window.addEventListener('resize', handleResize);
        window.requestAnimationFrame(() => {
          recalculateHeight();
        });
      }

      if (_details.current && _content.current) {
        // A hidden document produces no frames, so the rAF callback below
        // would only run once the tab becomes visible again — until then a
        // toggle would hang mid-animation, its finish timer never armed.
        // Off-screen there is nothing to animate: settle directly. #1700
        if ((state.isClosing || state.isOpening) && document.hidden) {
          onAnimationFinish(!!state.isOpening);
        } else {
          window.requestAnimationFrame(() => {
            if (state.isClosing) {
              _details.current?.getBoundingClientRect();
              _details.current?.classList.remove('open');
              timer.current = window.setTimeout(
                () => onAnimationFinish(false),
                duration,
              );
            } else if (state.isOpening) {
              _details.current?.getBoundingClientRect();
              _details.current?.classList.add('open');
              timer.current = window.setTimeout(
                () => onAnimationFinish(true),
                duration,
              );
            }
          });
        }
      }

      return () => {
        if (calculateHeight) {
          window.removeEventListener('resize', handleResize);
        }

        if (timer.current != null) {
          window.clearTimeout(timer.current);
          timer.current = null;
        }
      };
    });

    // State managed by parent
    if (open !== undefined) {
      if (!open && state.isOpen) {
        doClose();
      } else if (open && !state.isOpen) {
        doOpen();
      }
    }

    // `--height` is only measured while this component renders, and after a
    // click the last of those renders is onAnimationFinish. Content that grows
    // later — async block data, lazily imported viewers, late images — would
    // stay clipped, so a settled panel sizes itself instead (#1700).
    const isSettledOpen = !!state.isOpen && !state.isOpening;

    return (
      <details
        ref={_details}
        className={
          className +
          (isSettledOpen || state.isClosing ? ' open' : '') +
          (isSettledOpen ? ' settled' : '')
        }
        open={state.isOpen || state.isClosing || undefined}
        name={name}
        {...props}
      >
        <summary ref={_summary} onClick={onClick} className={summaryClassName}>
          {summary}
        </summary>
        <div
          ref={_content}
          className={['details__content', contentClassName].join(' ')}
        >
          {children}
        </div>
      </details>
    );
  },
);
